import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./PhotoForm.module.css";

function PhotoForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [cars, setCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(true);
  const [carsError, setCarsError] = useState("");

  const [selectedCarId, setSelectedCarId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Фото успешно отправлены"
  );

  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser) return;

    let ignore = false;

    async function loadCars() {
      setCarsLoading(true);
      setCarsError("");

      try {
        // TODO: заменить на реальный endpoint списка машин
        const resp = await fetch("/api/photo/cars", {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "include",
        });

        const data = await safeReadResponse(resp);

        if (!resp.ok) {
          throw new Error(
            data?.message || data?.error || "Не удалось загрузить список машин"
          );
        }

        const list = Array.isArray(data?.cars) ? data.cars : [];

        if (!ignore) {
          setCars(list);
        }
      } catch (error) {
        if (!ignore) {
          setCarsError(error?.message || "Ошибка загрузки списка машин");
        }
      } finally {
        if (!ignore) {
          setCarsLoading(false);
        }
      }
    }

    loadCars();

    return () => {
      ignore = true;
    };
  }, [currentUser]);

  const isFormValid = useMemo(() => {
    return Boolean(selectedCarId) && photos.length > 0 && !submitting;
  }, [selectedCarId, photos.length, submitting]);

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";

    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }

    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Не удалось прочитать файл"));
        }
      };

      reader.onerror = () => reject(new Error("Ошибка чтения файла"));
      reader.readAsDataURL(file);
    });
  }

  async function resizeImageIfNeeded(file) {
    if (!file.type.startsWith("image/")) {
      return fileToDataUrl(file);
    }

    const dataUrl = await fileToDataUrl(file);

    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        const maxWidth = 1600;
        const shouldResize = img.width > maxWidth;

        if (!shouldResize) {
          resolve(dataUrl);
          return;
        }

        const ratio = maxWidth / img.width;
        const targetWidth = Math.round(img.width * ratio);
        const targetHeight = Math.round(img.height * ratio);

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(dataUrl);
          return;
        }

        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        const compressed = canvas.toDataURL("image/jpeg", 0.82);
        resolve(compressed);
      };

      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  function extractBase64(dataUrl) {
    const commaIndex = dataUrl.indexOf(",");
    return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  }

  const handleFilesPicked = async (event) => {
    clearError();

    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      const prepared = await Promise.all(
        files.map(async (file) => {
          const dataUrl = await resizeImageIfNeeded(file);

          return {
            id: `${file.name}-${file.size}-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2, 8)}`,
            name: file.name,
            mimeType: file.type || "image/jpeg",
            size: file.size,
            previewUrl: dataUrl,
            base64: extractBase64(dataUrl),
          };
        })
      );

      setPhotos((prev) => [...prev, ...prepared]);
      event.target.value = "";
    } catch (error) {
      setErrorTitle("Ошибка файлов");
      setErrorMessage(error?.message || "Не удалось обработать изображения");
    }
  };

  const handleRemovePhoto = (photoId) => {
    setPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearError();

    if (!selectedCarId) {
      setErrorTitle("Не выбрана машина");
      setErrorMessage("Выберите машину перед отправкой.");
      return;
    }

    if (!photos.length) {
      setErrorTitle("Нет фотографий");
      setErrorMessage("Добавьте хотя бы одну фотографию.");
      return;
    }

    setSubmitting(true);

    try {
      const resp = await fetch("/api/photo/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          carId: selectedCarId,
          photos: photos.map((photo) => photo.base64),
        }),
      });

      const data = await safeReadResponse(resp);

      if (!resp.ok) {
        throw new Error(
          data?.details ||
            data?.message ||
            data?.error ||
            "Не удалось отправить фотографии"
        );
      }

      setSuccessMessage(
        data?.details || data?.message || "Фото успешно отправлены"
      );
      setSuccessOpen(true);
      setPhotos([]);
      setSelectedCarId("");
    } catch (error) {
      setErrorTitle("Ошибка отправки");
      setErrorMessage(error?.message || "Не удалось отправить фотографии");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!successOpen) return;
    const timer = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(timer);
  }, [successOpen, navigate]);

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>
              Переход в кабинет через 3 секунды...
            </p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.topControls}>
            <div className={styles.inputField}>
              <select
                id="carSelect"
                value={selectedCarId}
                onChange={(e) => setSelectedCarId(e.target.value)}
                className={styles.select}
                disabled={carsLoading || submitting}
                required
              >
                <option value="">
                  {carsLoading ? "Загрузка машин..." : "Выберите машину"}
                </option>

                {cars.map((car) => (
                  <option key={car.id} value={car.id}>
                    {car.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className={styles.backButton}
              type="button"
            >
              Назад
            </button>
          </div>

          <div className={styles.uploadSection}>
            <div className={styles.uploadButtons}>
              <label className={styles.uploadButton}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFilesPicked}
                  className={styles.hiddenInput}
                />
                Выбрать из галереи
              </label>

              <label className={styles.uploadButton}>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleFilesPicked}
                  className={styles.hiddenInput}
                />
                Сделать фото
              </label>
            </div>
          </div>

          {photos.length > 0 && (
            <div className={styles.previewGrid}>
              {photos.map((photo) => (
                <div key={photo.id} className={styles.previewCard}>
                  <img
                    src={photo.previewUrl}
                    alt={photo.name}
                    className={styles.previewImage}
                  />

                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemovePhoto(photo.id)}
                    aria-label="Удалить фотографию"
                    title="Удалить"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && (
                  <p className={styles.alertSubText}>{errorMessage}</p>
                )}
              </div>
              <button
                type="button"
                className={styles.alertButton}
                onClick={clearError}
              >
                Понятно
              </button>
            </div>
          )}

          <button
            type="submit"
            className={styles.sendButton}
            disabled={!isFormValid}
          >
            {submitting ? "Отправка..." : "Отправить фотографии"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PhotoForm;
